
import L from 'leaflet';
import 'leaflet.markercluster';
import "leaflet.featuregroup.subgroup";
import {computed, defineComponent, onMounted, watch} from "vue";
import {latLngBounds, latLng, Map} from "leaflet";
import {useStoreComposition} from "@/composables/StoreComposition";
import {useComponentDimensions} from "@/composables/DimensionsComposition";
import {configuration} from "@/composables/ConfigurationComposition";

export default defineComponent({
  setup() {
    let leafletMap;
    let tooltipLayers = [];

    const bounds = computed(() => {
      const positions = useStoreComposition.stores.map(store => {
        return latLng(parseFloat(store.latitude), parseFloat(store.longitude));
      });

      return latLngBounds(positions);
    });

    onMounted(() => {
      leafletMap = new Map('map', {
        zoomControl: false,
        zoomSnap: 0.1
      });
      leafletMap.scrollWheelZoom.disable();

      // @ts-ignore
      const markerClusterGroup = L.markerClusterGroup({
        showCoverageOnHover: false,
        maxClusterRadius: 30,
        zoomToBoundsOnClick: false
      });
      markerClusterGroup.addTo(leafletMap)

      const icons = {} as Object;
      (configuration.resultTypes ?? []).forEach(resulttype => {
        // @ts-ignore
        icons[resulttype.key] = resulttype;
      });

      L.control.zoom({
        position: 'topright'
      }).addTo(leafletMap);

      L.tileLayer(configuration.leafletTileSrc, {
        attribution: configuration.leafletAttribution
      }).addTo(leafletMap);

      // initial bounds of country
      leafletMap.fitBounds([configuration.bounding['sw'], configuration.bounding['ne']], {
        paddingTopLeft: [useComponentDimensions.offset.value.left, useComponentDimensions.offset.value.top],
        paddingBottomRight: [useComponentDimensions.offset.value.right, useComponentDimensions.offset.value.bottom],
        animate: false,
        maxZoom: 14
      });

      // react on offset change (resize)
      watch(useComponentDimensions.offset, (offset) => {
        const valid = (bounds.value && bounds.value.isValid());
        let b = bounds.value;

        let opt = {
          paddingTopLeft: [offset.left, offset.top],
          paddingBottomRight: [offset.right, offset.bottom],
          animate: false
        };

        if (!valid) {
          // @ts-ignore
          b = [configuration.bounding['sw'], configuration.bounding['ne']];
          opt['maxZoom'] = 14;
        }

        leafletMap.fitBounds(b, opt);
      });

      watch(bounds, bounds => {
        if (bounds.isValid()) {
          leafletMap.fitBounds(bounds, {
            paddingTopLeft: [useComponentDimensions.offset.value.left, useComponentDimensions.offset.value.top],
            paddingBottomRight: [useComponentDimensions.offset.value.right, useComponentDimensions.offset.value.bottom],
            animate: true,
            duration: 2
          });
        }
      });

      watch(useStoreComposition.storesRef, (stores) => {
        if (tooltipLayers !== undefined) {
          tooltipLayers.forEach(tooltipGroup => {
            leafletMap.removeLayer(tooltipGroup);
          });
        }

        tooltipLayers = [];

        markerClusterGroup.clearLayers()

        leafletMap.invalidateSize();

        stores.forEach(store => {
          try {
            const iconConfig = icons[store.type].inactiveIcon;
            const icon = L.icon({
              iconUrl: iconConfig.image,
              shadowUrl: '',
              iconSize: [iconConfig.width * configuration.inactiveMarkerScaleFactor, iconConfig.height * configuration.inactiveMarkerScaleFactor],
              iconAnchor: [iconConfig.width * 0.5 * configuration.inactiveMarkerScaleFactor, iconConfig.height * configuration.inactiveMarkerScaleFactor],
            });

            store.marker = L.marker(latLng(parseFloat(store.latitude), parseFloat(store.longitude)), {
              icon
            });
          } catch (e) {
            store.marker = L.marker(latLng(parseFloat(store.latitude), parseFloat(store.longitude)))
          }

          store.marker.on('click', () => {
            if (useStoreComposition.activeStore.value !== null) {
              useStoreComposition.activeStore.value.isActive = false;
            }

            store.isActive = true;
          });

          // @ts-ignore
          const tooltipLayer = L.featureGroup.subGroup(markerClusterGroup, [store.marker]);

          if (configuration.enableTooltip === true) {
            tooltipLayer.bindTooltip(function () {
              return configuration.tooltipTemplate(store);
            }, {
              offset: [0, (32 * configuration.inactiveMarkerScaleFactor + 10) * -1],
              direction: 'top',
              className: store.type
            });

            tooltipLayer.addTo(leafletMap)
            // @ts-ignore
            tooltipLayers.push(tooltipLayer)
          } else {
            tooltipLayer.addTo(leafletMap)
          }
        });
      });

      /**
       * handle marker updates
       *
       * consider following states:
       *
       * - select store while no other is opened
       * - select store while another is opened
       * - close store while itself is opened
       * - close store while anothe ris opened
       */
      watch(useStoreComposition.activeStore, (store, prev) => {
        if ((prev !== null && prev.marker) && (store !== null && store.marker)) {
          // a store has been opened while another is still open
          try {
            const iconConfig = icons[prev.type].inactiveIcon;
            const icon = L.icon({
              iconUrl: iconConfig.image,
              shadowUrl: '',
              iconSize: [iconConfig.width * configuration.inactiveMarkerScaleFactor, iconConfig.height * configuration.inactiveMarkerScaleFactor],
              iconAnchor: [iconConfig.width * 0.5 * configuration.inactiveMarkerScaleFactor, iconConfig.height * configuration.inactiveMarkerScaleFactor],
            });

            prev.marker.setIcon(icon);
          } catch (e) {
            // do nothing
          }

          try {
            const iconConfig = icons[store.type].activeIcon;
            const icon = L.icon({
              iconUrl: iconConfig.image,
              shadowUrl: '',
              iconSize: [iconConfig.width * configuration.activeMarkerScaleFactor, iconConfig.height * configuration.activeMarkerScaleFactor],
              iconAnchor: [iconConfig.width * 0.5 * configuration.activeMarkerScaleFactor, iconConfig.height * configuration.activeMarkerScaleFactor],
            });

            store.marker.setIcon(icon);
          } catch (e) {
            // to nothing
          }
        } else if ((prev !== null && prev.marker) && (store === null)) {
          // a opened store has been closed while no other is opened
          try {
            const iconConfig = icons[prev.type].inactiveIcon;
            const icon = L.icon({
              iconUrl: iconConfig.image,
              shadowUrl: '',
              iconSize: [iconConfig.width * configuration.inactiveMarkerScaleFactor, iconConfig.height * configuration.inactiveMarkerScaleFactor],
              iconAnchor: [iconConfig.width * 0.5 * configuration.inactiveMarkerScaleFactor, iconConfig.height * configuration.inactiveMarkerScaleFactor],
            });

            prev.marker.setIcon(icon);
          } catch (e) {
            // do nothing
          }
        } else if ((store !== null && store.marker) && (prev === null)) {
          // a store has been opened while no other is still open
          try {
            const iconConfig = icons[store.type].activeIcon;
            const icon = L.icon({
              iconUrl: iconConfig.image,
              shadowUrl: '',
              iconSize: [iconConfig.width * configuration.activeMarkerScaleFactor, iconConfig.height * configuration.activeMarkerScaleFactor],
              iconAnchor: [iconConfig.width * 0.5 * configuration.activeMarkerScaleFactor, iconConfig.height * configuration.activeMarkerScaleFactor],
            });

            store.marker.setIcon(icon);
          } catch (e) {
            // to nothing
          }
        }

        if (store !== null) {
          leafletMap.fitBounds(latLngBounds([latLng(parseFloat(store.latitude), parseFloat(store.longitude))]), {
            paddingTopLeft: [useComponentDimensions.offset.value.left, useComponentDimensions.offset.value.top],
            paddingBottomRight: [useComponentDimensions.offset.value.right, useComponentDimensions.offset.value.bottom],
            animate: true,
            duration: 1,
            maxZoom: 14
          });
        } else {
          if (bounds.value.isValid()) {
            leafletMap.fitBounds(bounds.value, {
              paddingTopLeft: [useComponentDimensions.offset.value.left, useComponentDimensions.offset.value.top],
              paddingBottomRight: [useComponentDimensions.offset.value.right, useComponentDimensions.offset.value.bottom],
              animate: true,
              duration: 2
            });
          }
        }
      });

      document.addEventListener('DOMContentLoaded', () => {
        leafletMap.invalidateSize();
      }, false);
    });

    return {
      useComponentDimensions
    };
  },
  mounted() {
    this.useComponentDimensions.mapElement = this.$refs.mapElement as HTMLElement;
  }
});
